import { useEffect } from "react";
import { acquireMicrophone, releaseMicrophone } from "~/lib/audio";
import { currentTime, LIVE_TYPES, useGraphStore } from "~/store/graphStore";

/** Matches the 50 ms interval the rete version drove Animate/Audio nodes with. */
const MIN_FRAME_MS = 50;

const selectIsLive = (state: { nodes: { type: string }[] }): boolean =>
  state.nodes.some((node) => LIVE_TYPES.has(node.type));

const selectNeedsAudio = (state: { nodes: { type: string }[] }): boolean =>
  state.nodes.some((node) => node.type === "audio");

/**
 * Re-runs the graph while it contains time-varying nodes, and holds the
 * microphone open only while an Audio node is on the canvas.
 */
export function useGraphClock(): void {
  const isLive = useGraphStore(selectIsLive);
  const needsAudio = useGraphStore(selectNeedsAudio);
  const run = useGraphStore((state) => state.run);

  useEffect(() => {
    if (!needsAudio) return;

    acquireMicrophone();
    return releaseMicrophone;
  }, [needsAudio]);

  useEffect(() => {
    if (!isLive) return;

    let frame = 0;
    let last = 0;

    const loop = (): void => {
      frame = requestAnimationFrame(loop);

      const now = performance.now();
      if (now - last < MIN_FRAME_MS) return;

      last = now;
      run(currentTime());
    };

    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [isLive, run]);
}
